import { Storage } from "./Storage.mjs";
import { TaskQueue } from "./TaskQueue.mjs";
import { Transaction } from "./transaction/Transaction.mjs";
import { WriteOpsApi } from "./types.mjs";
import { isArray } from "@repo/utils/arrayUtils";
import { isEqual, pick, sortBy } from "@repo/utils/objectUtils";
import { unreachable } from "@repo/utils/typeUtils";
import { InMemoryTupleStorage, TupleDatabase, TupleDatabaseClient, subscribeQuery } from "tuple-database";

//#region src/Database.ts
var Database = class Database {
	tupleDb = new TupleDatabaseClient(new TupleDatabase(new InMemoryTupleStorage()));
	storage;
	logger;
	rng;
	ready;
	constructor({ logger, storage: storageAdapter, rng }) {
		this.logger = logger;
		this.storage = storageAdapter ? new Storage(storageAdapter, (error) => {
			this.logger.error("Storage error", error);
		}) : void 0;
		this.rng = rng;
		this.ready = this.storage ? this.loadFromStorage(this.storage) : Promise.resolve();
	}
	async clear() {
		const writeOps = this.tupleDb.scan({}).reduce((ops, { key }) => {
			ops.remove = ops.remove || [];
			ops.remove.push(key);
			return ops;
		}, {});
		if (writeOps.remove?.length) this.tupleDb.commit(writeOps);
		await this.storage?.clear();
	}
	/**
	* What if values in storage changes?
	* What if storage too big to load all at once?
	*/
	async loadFromStorage(storage) {
		this.logger.info("Loading from storage");
		const results = await storage.scan();
		this.tupleDb.commit({ set: results });
		let writeOpsQueue = {};
		const storageWriteQueue = new TaskQueue({ commitToStorage: async () => {
			this.logger.info("Committing to storage...");
			const copy = writeOpsQueue;
			writeOpsQueue = {};
			try {
				await storage.commit(copy);
				this.logger.info("Committed to storage");
			} catch (error) {
				this.logger.error("Error committing to storage", error);
				writeOpsQueue = copy;
			}
		} }, 120);
		this.tupleDb.subscribe({}, (writeOps) => {
			writeOpsQueue = WriteOpsApi.merge(writeOpsQueue, writeOps);
			storageWriteQueue.enqueue("commitToStorage").catch((error) => {
				this.logger.error("Error committing to storage", error);
			});
		});
	}
	makeTupleDbTransaction() {
		return this.tupleDb.transact(this.rng.randomId());
	}
	transact() {
		return new Transaction(this.tupleDb.transact(this.rng.randomId()));
	}
	commit(transaction) {
		transaction.tupleDbTx.commit();
	}
	subscribe(query, callback) {
		return subscribeQuery(this.tupleDb, (db) => Database.runQuery(query, db), callback);
	}
	run(query) {
		return Database.runQuery(query, this.tupleDb);
	}
	static runQuery(query, tupleDb) {
		const { collection, limit, order, select, where } = query.build();
		let results = tupleDb.scan({
			gte: [
				"record",
				collection,
				null
			],
			lte: [
				"record",
				collection,
				true
			]
		}).map(({ value }) => value);
		if (where) results = results.filter((value) => {
			return where.every(([attribute, operator, valueToTestAgainst]) => {
				const fieldValue = value[attribute];
				switch (operator) {
					case "=": return isEqual(fieldValue, valueToTestAgainst);
					case ">": return fieldValue > valueToTestAgainst;
					case "<": return fieldValue < valueToTestAgainst;
					case ">=": return fieldValue >= valueToTestAgainst;
					case "<=": return fieldValue <= valueToTestAgainst;
					default: unreachable(operator);
				}
			});
		});
		if (order) results = sortBy(results, ...order.map(([attribute, direction]) => [(item) => item[attribute], direction]));
		if (isArray(select)) results = results.map((value) => pick(value, select));
		if (limit) results = results.slice(0, limit);
		return results;
	}
};

//#endregion
export { Database };